import { serialize } from 'object-to-formdata'
import Qs from 'qs'

import { callUrl } from '@services/common/api'

import { injectCompanyToURL } from '@helpers'
import { serializeParamsWithCustomFields } from '@services/helpers'

import type { InvoiceDownloadRequestParams } from '@components/dialogs/InvoiceDownloadDialog'

import Api from '../api'
import type { ExpenseCreateByUploadPayload, ExpenseUpdatePayload, UpdateExpenseDetailsFormValues } from './types'
import {
  API_BULK_CATEGORIZATION_URL,
  API_EXPENSE_ARTIFACT_DELETE_URL,
  API_EXPENSE_ARTIFACT_DOWNLOAD_URL,
  API_EXPENSE_DELETE_URL,
  API_EXPENSE_DETAILS_URL,
  API_EXPENSE_EVENTS_LOG_URL,
  API_EXPENSE_LIST_URL,
  API_EXPENSE_STOP_RECOGNITION_URL,
  API_EXPENSE_TYPE_URL,
  API_EXPENSE_USER_VIEW_URL,
  API_FILING_NUMBER_URL,
  API_INVOICE_NUMBER_CHECK_URL,
  API_INVOICE_RECOMMENDATION_URL,
  API_JOB_NUMBER_LIST_URL,
  API_JOB_NUMBER_URL,
  API_MISSING_INVOICES_URL,
  V2_API_EXPENSE_CHARTS_URL,
  V2_API_EXPENSE_COUNT_URL,
  V2_API_EXPENSE_CREATE_WITH_RECOGNITION_URL,
  V2_API_EXPENSE_LIST_URL,
  V2_API_EXPENSE_REPLACE_ARTIFACT_URL,
  V2_API_EXPENSE_UPDATE_WITH_RECOGNITION_URL,
  V2_API_EXPENSES_BACKGROUND_ACTION_URL,
  V2_API_INIT_EXPENSE_DOWNLOAD_URL,
  V2_API_LEDGER_NUMBER_RECOMMENDATION_URL,
} from './urls'

export function fetchExpenseDetails(companyId: number, expenseId: ItemIdType) {
  return Api.get(injectCompanyToURL(API_EXPENSE_DETAILS_URL, companyId).replace('{expense_id}', String(expenseId)))
}

export function userViewExpense(companyId: number, expenseId: number) {
  return Api.post(injectCompanyToURL(API_EXPENSE_USER_VIEW_URL, companyId).replace('{expense_id}', String(expenseId)))
}

// recommendation
export function updateExpenseWithRecommendation({
  company,
  ...payload
}: Record<string, unknown> & { company: number }) {
  return Api.post(injectCompanyToURL(API_INVOICE_RECOMMENDATION_URL, company), payload)
}

// CREATE EXPENSE
//* expense artifact upload - 3 paths: create with recognition, update with recognition, upload with artifact replace
export function createExpenseRecognition(companyId: Company['id'], { file }: ExpenseCreateByUploadPayload) {
  return Api.post(
    injectCompanyToURL(V2_API_EXPENSE_CREATE_WITH_RECOGNITION_URL, companyId),
    { file },
    {
      timeout: 5 * 60000, // 5 * 60sec
      transformRequest: [
        function (data) {
          return serialize(data)
        },
      ],
    }
  )
}

export function updateExpenseRecognition(companyId: Company['id'], { invoice, file }: ExpenseUpdatePayload) {
  return Api.post(
    injectCompanyToURL(V2_API_EXPENSE_UPDATE_WITH_RECOGNITION_URL, companyId).replace('{expense_id}', String(invoice)),
    { file },
    {
      timeout: 5 * 60000, // 5 * 60sec
      transformRequest: [
        function (data) {
          return serialize(data)
        },
      ],
    }
  )
}

export function replaceExpenseArtifact(
  companyId: Company['id'],
  { invoice, file }: { file: File; invoice: ItemIdType }
) {
  return Api.post(
    injectCompanyToURL(V2_API_EXPENSE_REPLACE_ARTIFACT_URL, companyId).replace('{expense_id}', String(invoice)),
    { file },
    {
      timeout: 5 * 60000, // 5 * 60sec
      transformRequest: [
        function (data) {
          return serialize(data)
        },
      ],
    }
  )
}

export function expenseStopRecognition({ companyId, invoice }: { companyId: Company['id']; invoice: ItemIdType }) {
  return Api.post<void>(
    injectCompanyToURL(API_EXPENSE_STOP_RECOGNITION_URL, companyId).replace('{expense_id}', String(invoice))
  )
}

export function removeExpenseArtifact(companyId: number, data: { id: ItemIdType }) {
  return Api.post(injectCompanyToURL(API_EXPENSE_ARTIFACT_DELETE_URL, companyId), data)
}

// by form post
export function createExpense(companyId: number, data: ExpenseDetailsFormInitialValues) {
  return Api.post(injectCompanyToURL(API_EXPENSE_LIST_URL, companyId), data)
}

// update
export function updateExpense(companyId: number, { id, ...data }: UpdateExpenseDetailsFormValues) {
  return Api.patch(injectCompanyToURL(API_EXPENSE_DETAILS_URL, companyId).replace('{expense_id}', String(id)), data)
}

export function removeExpense(
  companyId: number,
  { expenseId, ...data }: { expenseId: number; source: ExpenseDeleteSource }
) {
  return Api.post(
    injectCompanyToURL(API_EXPENSE_DELETE_URL, companyId).replace('{expense_id}', String(expenseId)),
    data
  )
}

export function fetchExpenseTypes(companyId: number) {
  return Api.get(injectCompanyToURL(API_EXPENSE_TYPE_URL, companyId))
}

export function createExpenseType(data: Record<string, unknown> & { company: number }) {
  return Api.post(injectCompanyToURL(API_EXPENSE_TYPE_URL, data.company), data)
}

// BULK UPDATE CHECK
export function bulkInvoiceUpdateCheck(
  companyId: number,
  { partner_id, payment_method, id: invoiceId }: UpdateExpenseDetailsFormValues
) {
  return Api.get(
    injectCompanyToURL(API_MISSING_INVOICES_URL, companyId)
      .replace('{partner_id}', String(partner_id))
      .replace('{payment_method}', String(payment_method))
      .replace('{invoice_id}', String(invoiceId))
  )
}

export function bulkInvoiceUpdate({
  count,
  company_id,
  partner_id,
  payment_method,
  ...data
}: Record<string, unknown> & {
  id: number
  company_id: number
  partner_id: number
  payment_method: string
}) {
  return Api.patch(
    injectCompanyToURL(API_MISSING_INVOICES_URL, company_id)
      .replace('{partner_id}', String(partner_id))
      .replace('{payment_method}', String(payment_method))
      .replace('{invoice_id}', String(data.id)),
    data
  )
}

// EVENTS LOG

export function fetchEventsLog(companyId: number, expenseId: number) {
  return Api.get(injectCompanyToURL(API_EXPENSE_EVENTS_LOG_URL, companyId).replace('{expense_id}', String(expenseId)))
}

// CATEGORIZATION
export function bulkCategorization(companyId: number, data: Record<string, unknown>) {
  return Api.post(injectCompanyToURL(API_BULK_CATEGORIZATION_URL, companyId), data)
}

// DOWNLOADS
export function downloadExpenseArtifact(
  companyId: number,
  { id, ...data }: InvoiceDownloadRequestParams & { id: number }
) {
  return Api.post(
    injectCompanyToURL(API_EXPENSE_ARTIFACT_DOWNLOAD_URL, companyId).replace('{invoice_id}', String(id)),
    data
  )
}

export function invoiceNumberCheck(companyId: number, data: Record<string, unknown>) {
  return Api.post(injectCompanyToURL(API_INVOICE_NUMBER_CHECK_URL, companyId), data)
}

//* V2 API

export function fetchExpenseListV2(companyId: number, params: Partial<ExpenseListStoreFilters>) {
  return Api.get(injectCompanyToURL(V2_API_EXPENSE_LIST_URL, companyId), {
    params,
    paramsSerializer: serializeParamsWithCustomFields,
  })
}

export function fetchExpenseCountV2(companyId: number, params: Partial<ExpenseListStoreFilters>) {
  return Api.get(injectCompanyToURL(V2_API_EXPENSE_COUNT_URL, companyId), {
    params,
    paramsSerializer: serializeParamsWithCustomFields,
  })
}

export function fetchExpenseCharts(companyId: number, params: Partial<ExpenseListStoreFilters>) {
  return Api.get(injectCompanyToURL(V2_API_EXPENSE_CHARTS_URL, companyId), {
    params,
    paramsSerializer: serializeParamsWithCustomFields,
  })
}

//* V2
export function fetchExpenseListByPagingV2(url: string) {
  return callUrl({ method: 'get', url })
}

export function initDownloadV2(companyId: number, params: unknown) {
  return Api.get(injectCompanyToURL(V2_API_INIT_EXPENSE_DOWNLOAD_URL, companyId), {
    params,
    paramsSerializer: {
      serialize(params) {
        return Qs.stringify(params, { indices: false })
      },
    },
  })
}

//* SPECIAL BACKGROUND-ACTION API
export function expensesBackgroundAction(companyId: number, payload: BackgroundActionPayload) {
  return Api.post<BackgroundActionResponse>(
    injectCompanyToURL(V2_API_EXPENSES_BACKGROUND_ACTION_URL, companyId),
    payload
  )
}

export function callUrlByPostMethod(url: string) {
  return callUrl({ method: 'post', url })
}

export function fetchLastFilingNumber(companyId: number) {
  return Api.get(injectCompanyToURL(API_FILING_NUMBER_URL, companyId))
}

//* JobNumber
export function createJobNumber(companyId: number, name: string) {
  return Api.post(injectCompanyToURL(API_JOB_NUMBER_URL, companyId), { name })
}

export function searchJobNumbers(companyId: number, search: string) {
  return Api.get(injectCompanyToURL(API_JOB_NUMBER_LIST_URL, companyId), {
    params: { search },
  })
}

export function fetchLedgerNumberRecommendations(companyId: number, expenseTypeId: number) {
  return Api.get(
    injectCompanyToURL(V2_API_LEDGER_NUMBER_RECOMMENDATION_URL, companyId).replace(
      ':expense_type_id',
      String(expenseTypeId)
    )
  )
}
